import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { routes } from "../../routes/routes";
import { Invitation } from "../../types";
import { getUserAvatar } from "../../utils/getMedia";
import handleImgError from "../../handleImgError";

interface PromoterGroupPhotoStripProps {
  invitations: Invitation[];
  city: string | undefined;
  groupId: string | undefined;
  callbackUrl: string;
  className?: string;
  trackClassName?: string;
  imageClassName?: string;
  onPhotoClick?: () => void;
}

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const PromoterGroupPhotoStrip: React.FC<PromoterGroupPhotoStripProps> = ({
  invitations,
  city,
  groupId,
  callbackUrl,
  className = "yellowTxt d-flex carousel-container",
  trackClassName = "carousel-seen d-flex d-flex-wrap",
  imageClassName = "m-2 imgGuest",
  onPhotoClick,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Two booleans, not a scroll position. 1px tolerance avoids subpixel bugs
  // where scrollLeft + clientWidth never exactly equals scrollWidth.
  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  // Observe the inner TRACK, not just the scroller: when guest images load in
  // they widen the track (scrollWidth grows) while the scroller's own size is
  // unchanged, so an observer on the scroller alone would never reveal the
  // right arrow. Observing both covers image load, viewport and count changes.
  useEffect(() => {
    updateArrows();
    const observer = new ResizeObserver(() => updateArrows());
    if (scrollRef.current) observer.observe(scrollRef.current);
    if (trackRef.current) observer.observe(trackRef.current);
    return () => observer.disconnect();
  }, [updateArrows, invitations.length]);

  // Coalesce scroll updates to one per frame and cancel any pending frame on
  // unmount so updateArrows never runs after the component is gone.
  const handleScroll = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      updateArrows();
    });
  }, [updateArrows]);

  useEffect(
    () => () => {
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
      }
    },
    []
  );

  const scrollByPage = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction * el.clientWidth * 0.8,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  };

  return (
    <div className="photo-carousel">
      {canScrollLeft && (
        <button
          type="button"
          className="photo-carousel__arrow photo-carousel__arrow--left"
          aria-label="Previous photos"
          onClick={() => scrollByPage(-1)}
        >
          <span aria-hidden="true">&#8249;</span>
        </button>
      )}

      <div ref={scrollRef} className={className} onScroll={handleScroll}>
        <div ref={trackRef} className={trackClassName}>
          {invitations.map((invitation, index) => (
            <Link
              to={routes.PhotoZoom(
                callbackUrl,
                city ?? "0",
                groupId ?? invitation.id,
                `${index}?photo=${getUserAvatar(invitation.id)}`
              )}
              key={invitation.id}
              onClick={(event) => {
                event.stopPropagation();
                onPhotoClick?.();
              }}
            >
              <img
                src={getUserAvatar(invitation.id)}
                alt={invitation.firstName || "Profile Photo"}
                className={imageClassName}
                draggable={false}
                onError={(event) => handleImgError(event, invitation.gender.toString())}
              />
            </Link>
          ))}
        </div>
      </div>

      {canScrollRight && (
        <button
          type="button"
          className="photo-carousel__arrow photo-carousel__arrow--right"
          aria-label="Next photos"
          onClick={() => scrollByPage(1)}
        >
          <span aria-hidden="true">&#8250;</span>
        </button>
      )}
    </div>
  );
};

export default PromoterGroupPhotoStrip;
